// /Users/yitk/ecomoria-ai/src/web/client/src/App.jsx
// ======= הגנה גלובלית על fetch - יש למקם ממש בתחילת הקובץ, לפני כל ייבוא =======
if (typeof window !== 'undefined' && !window.__UNDEFINED_PROTECTION_ENABLED) {
  (function() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      if (url && typeof url === 'string' && url.includes('=undefined')) {
        console.warn('🚫 Blocked API call with undefined:', url);
        return Promise.resolve({
          ok: false,
          status: 400,
          statusText: 'Bad Request - undefined parameter',
          json: () => Promise.resolve({ error: 'Invalid request parameters' }),
          text: () => Promise.resolve('Invalid request parameters')
        });
      }
      return originalFetch.apply(this, args);
    };
    window.__UNDEFINED_PROTECTION_ENABLED = true;
    console.log('✅ Undefined protection enabled');
  })();
}

// ======= תוספת API PROTECTION מתקדמת - אחרי ההגנה הקודמת =======
(function setupAPIProtection() {
  if (typeof window === 'undefined' || window.__API_PROTECTION_ENABLED) return;
  const originalFetch = window.fetch;
  
  window.fetch = function(url, options) {
    const urlString = url.toString();
    
    // חסימת קריאות לא חוקיות (but allow JWT tokens that end with =)
    const hasInvalidParams = 
      urlString.includes('=undefined') ||
      urlString.includes('=null') ||
      urlString.includes('=&');
    // Removed: urlString.endsWith('=') - this blocks valid JWT tokens
    
    if (hasInvalidParams) {
      console.warn('🚫 Blocked invalid API call:', urlString);
      return Promise.resolve(new Response(
        JSON.stringify({ error: 'Invalid request parameters' }),
        {
          status: 400,
          statusText: 'Bad Request',
          headers: { 'Content-Type': 'application/json' }
        }
      ));
    }
    // מוסיף shop_domain אוטומטי אם חסר ב-URL של ה-API.
    // Only a REAL stored domain is ever injected — never a demo fallback.
    // If we don't know the shop, the call goes out as-is and the backend
    // answers 400; showing demo-shop data as if it were the merchant's own
    // store is a data-integrity violation.
    if (urlString.includes('/api/') && !urlString.includes('shop_domain')) {
      const shopDomain =
        localStorage.getItem('shop_domain') ||
        localStorage.getItem('shopDomain');
      if (shopDomain && shopDomain !== 'undefined' && shopDomain !== 'null') {
        const separator = urlString.includes('?') ? '&' : '?';
        const newUrl = `${urlString}${separator}shop_domain=${shopDomain}`;
        return originalFetch.call(this, newUrl, options);
      }
    }
    return originalFetch.apply(this, arguments);
  };
  window.__API_PROTECTION_ENABLED = true;
  console.log('✅ API Protection enabled');
})();

// ======= IMPORTS =======
import React, { useEffect, useState } from 'react';
import './App.css';
import Pages from '@/pages/index.jsx';
import { Toaster } from '@/components/ui/toaster';
import ShopifyAppBridge from './components/ShopifyAppBridge';
import { storage } from '@/utils/storage';

function App() {
  const [isReady, setIsReady] = useState(false);

  // Shopify OAuth handling
  useEffect(() => {
    const handleShopifyParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const shop = urlParams.get('shop');
      const host = urlParams.get('host');
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      console.log('🔍 URL params:', { shop, host, fullUrl: window.location.href });
      
      // If we have Shopify OAuth callback params
      if (code && state && shop) {
        console.log('🔐 OAuth callback detected, redirecting to backend...');
        window.location.href = `https://api.ecomoria.com/auth/callback?${urlParams.toString()}`;
        return;
      }
      
      // If we have shop param but no code (initial app load from Shopify).
      // /privacy is a public document — never bounce its visitors to Shopify
      // Admin even if the URL happens to carry a ?shop=... query param.
      const isPublicDocPath = window.location.pathname.startsWith('/privacy');
      if (shop && !window.location.pathname.includes('/auth/') && !isPublicDocPath) {
        console.log('🛍️ Shopify shop detected:', shop);
        
        // Store shop domain
        localStorage.setItem('shop_domain', shop);
        localStorage.setItem('shopDomain', shop);
        if (host) {
          localStorage.setItem('shopify_host', host);
        }
        
        // Check if we're in embedded mode (inside Shopify Admin iframe)
        const isEmbedded = window.top !== window.self;
        console.log('🖼️ Embedded mode:', isEmbedded);
        
        // If NOT embedded but we have shop, redirect to Shopify Admin
        if (!isEmbedded && shop) {
          const storeHandle = shop.replace('.myshopify.com', '');
          const shopifyAdminUrl = `https://admin.shopify.com/store/${storeHandle}/apps/ecomoria-ai`;
          console.log('↩️ Not in iframe, redirecting to Shopify Admin:', shopifyAdminUrl);
          window.top.location.href = shopifyAdminUrl;
          return;
        }
        
        // If embedded, make sure we're not on landing page - keep query params!
        if (isEmbedded && window.location.pathname === '/') {
          const currentParams = window.location.search;
          window.location.href = '/dashboard' + currentParams;
        }
      }
    };
    
    handleShopifyParams();
  }, []);

  // App initialization
  useEffect(() => {
    const initializeApp = () => {
      console.log('🔧 Initializing App...');
      // NO silent demo fallback: if the domain is unknown we leave it unknown
      // and each page shows its honest "couldn't identify your store" state.
      // Demo mode remains available ONLY as an explicit choice (DemoModeButton
      // / the Login demo entry, which also set isDemo).
      const currentDomain = localStorage.getItem('shop_domain') || localStorage.getItem('shopDomain');
      if (currentDomain && currentDomain !== 'undefined' && currentDomain !== 'null') {
        localStorage.setItem('shop_domain', currentDomain);
        localStorage.setItem('shopDomain', currentDomain);
        console.log('✅ Shop data synchronized:', currentDomain);
      } else {
        console.log('ℹ️ No shop domain yet — waiting for Shopify params or explicit demo mode');
      }
      setIsReady(true);
    };

    const timer = setTimeout(initializeApp, 100);
    return () => clearTimeout(timer);
  }, []);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <ShopifyAppBridge>
      <Pages />
      <Toaster />
    </ShopifyAppBridge>
  );
}

export default App;